import { API } from 'aws-amplify';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import memberPlaceholder from '@gym-assets/images/staff-image-placeholder.jpg';
import { MemberDetailsModalContent } from '@gym-organisms/MemberDetailsModal/MemberDetailsModal';
import { toMindbodyMemberProfileImageUrl } from '@gym-particles/memberProfileImage';
import { parseMemberWaiverStatus } from '@gym-particles/types/Member';
import type { Member } from '@gym-particles/types/Member';
import type { GymType } from '@gym-particles/types/Gym';
import { UserRole } from '@gym-particles/types/User';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import { fetchGyms } from '@gym-redux/slices/gymsSlice';
import { getGymChainById } from '@gym-redux/slices/gymChainSlice';
import { fetchKisiDoors } from '@gym-redux/slices/kisiDoorSlice';
import { fetchAccessPoints } from '@gym-redux/slices/accessPointsSlice';
import {
  getEmployeeDashboardEventHistory,
  getEmployeeDashboardMemberships,
  getKisiEventsForSiteLocation,
  getMemberBookingDetails,
  getMemberByMemberId,
  getMembersForSiteLocation
} from '@gym-graphql/queries';
import { linkMemberToEvent } from '@gym-graphql/mutations';
import { onGymKeyEmployeeDashboardUpdate } from '@gym-graphql/subscriptions';
import type {
  EmployeeDashboardEvent,
  EmployeeDashboardEventCursorInput,
  EmployeeDashboardEventHistoryInput,
  GetEmployeeDashboardEventHistoryQuery,
  GetEmployeeDashboardMembershipsQuery,
  GetKisiEventsForSiteLocationQuery,
  GetMemberBookingDetailsQuery,
  GetMemberByMemberIdQuery,
  GetMembersForSiteLocationQuery,
  KisiEvent,
  SiteLocationMember
} from '@gym-src/API';
import { HardwareProvider as HardwareProviderValue } from '@gym-src/API';
import styles from '@gym-pages/DoorUnlockEventsDashboardPage/EmployeeDashboard.module.scss';
import { useEmployeeDashboardSubscription } from '@gym-pages/DoorUnlockEventsDashboardPage/useEmployeeDashboardSubscription';

dayjs.extend(utc);

const RECENT_EVENT_LIMIT_OPTIONS = [10, 15, 20] as const;
type RecentEventLimit = typeof RECENT_EVENT_LIMIT_OPTIONS[number];
const DEFAULT_RECENT_EVENT_LIMIT: RecentEventLimit = 10;
const HISTORY_PAGE_SIZE = 25;
const DASHBOARD_PANEL_ITEM_LIMIT = 2;
const LIVE_CHECK_IN_PANEL_ITEM_LIMIT = 4;

type FullScreenLayout = 'liveCheckIn' | 'withHistory';

type AsyncData<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: T }
  | { status: 'error' };

interface LiveEvent {
  id: number;
  siteId: number;
  siteLocationId: number;
  doorId: number;
  doorName: string;
  memberEmail: string;
  doorOpenedTime: string;
  doorOpenedTimeUtc: string;
  memberId?: number;
  memberName: string;
  memberUniqueId: string;
  memberClientId: string;
  memberStatus: string;
  memberAccountBalance: number;
  memberNote: string;
  memberWaiverStatus: string;
  memberWaiverAgreementDateTime: string;
  memberWaiverGraceUntil: string;
  memberWaiverPolicyEnforced: boolean;
  businessName: string;
  accessGranted: boolean;
  accessFailureCode?: string;
  accessFailureMessage?: string;
}

interface HistoryEvent extends LiveEvent {
  providerEventId?: string;
  accessProvider: string;
}

interface ActiveMembership {
  id: number;
  name: string;
  validFrom: string;
  validTo: string;
  scheduleType: string;
  remainingSessions?: number;
  remainingVisits?: number;
  siteLocationId: number;
  locationName: string;
  isProbableAccess: boolean;
  accessProvider?: string;
}

interface DashboardBooking {
  id: string;
  name: string;
  startDateTime: string;
  endDateTime: string;
  membershipName: string;
  grantsAccess: boolean;
}

interface MemberDashboardData {
  memberships: ActiveMembership[];
  appointments: DashboardBooking[];
}

interface LinkCandidate {
  id: number;
  name: string;
  uniqueId: string;
  clientId: string;
  email: string;
}

type OverlayState =
  | { kind: 'none' }
  | { kind: 'history' }
  | { kind: 'note'; note: string }
  | { kind: 'profile'; memberId: number }
  | { kind: 'link'; event: HistoryEvent | LiveEvent };

interface HistoryFilters {
  dateFrom: string;
  dateTo: string;
  memberId?: number;
  accessResult: 'all' | 'granted' | 'denied';
  search: string;
}

const emptyHistoryFilters: HistoryFilters = {
  dateFrom: '',
  dateTo: '',
  accessResult: 'all',
  search: ''
};

const parseRecentEventLimit = (value: string): RecentEventLimit =>
  RECENT_EVENT_LIMIT_OPTIONS.find((option) => option === Number(value)) ||
  DEFAULT_RECENT_EVENT_LIMIT;

const parseAccessResultFilter = (value: string): HistoryFilters['accessResult'] =>
  value === 'granted' ? 'granted' : value === 'denied' ? 'denied' : 'all';

const getTimeString = () => dayjs().format('dddd D MMMM h:mm a');

const compactValues = <T,>(items: Array<T | null> | null | undefined): T[] =>
  (items || []).flatMap((item) => (item ? [item] : []));

const parseEventMember = (event: Partial<KisiEvent | EmployeeDashboardEvent>) => ({
  memberId: event.memberId || undefined,
  memberName: event.memberName || '',
  memberUniqueId: event.memberUniqueId || '',
  memberClientId: event.memberClientId || '',
  memberStatus: event.memberStatus || '',
  memberAccountBalance: event.memberAccountBalance || 0,
  memberNote: event.memberNote || '',
  memberWaiverStatus: event.memberWaiverStatus || 'unknown',
  memberWaiverAgreementDateTime: event.memberWaiverAgreementDateTime || '',
  memberWaiverGraceUntil: event.memberWaiverGraceUntil || '',
  memberWaiverPolicyEnforced: event.memberWaiverPolicyEnforced === true
});

const parseKisiLiveEvent = (event: Partial<KisiEvent>): LiveEvent => ({
  id: event.id || 0,
  siteId: event.siteId || 0,
  siteLocationId: event.siteLocationId || 0,
  doorId: event.kisiDoorId || 0,
  doorName: event.kisiDoorName || '',
  memberEmail: event.doorOpenMemberEmail || '',
  doorOpenedTime: event.doorOpenedTime || '',
  doorOpenedTimeUtc: event.doorOpenedTime || '',
  ...parseEventMember(event),
  businessName: event.businessName || '',
  accessGranted: event.accessGranted !== false,
  accessFailureCode: event.accessFailureCode || undefined,
  accessFailureMessage: event.accessFailureMessage || undefined
});

const compareLiveEventRecency = (first: LiveEvent, second: LiveEvent): number =>
  first.doorOpenedTime.localeCompare(second.doorOpenedTime) || first.id - second.id;

const compareGymKeyEventRecency = (first: LiveEvent, second: LiveEvent): number =>
  dayjs.utc(first.doorOpenedTimeUtc).valueOf() - dayjs.utc(second.doorOpenedTimeUtc).valueOf() ||
  first.id - second.id;

const getNewestLiveEvent = (
  events: LiveEvent[],
  compare = compareLiveEventRecency
): LiveEvent | undefined =>
  events.reduce<LiveEvent | undefined>(
    (newest, event) => (!newest || compare(event, newest) > 0 ? event : newest),
    undefined
  );

const mergeDashboardEvents = <Event extends LiveEvent>(
  current: Event[],
  incoming: Event[],
  limit: number
): Event[] =>
  [...incoming, ...current]
    .reduce<Event[]>((ordered, event) => {
      if (ordered.some((existing) => existing.id === event.id)) return ordered;
      const index = ordered.findIndex((existing) => compareGymKeyEventRecency(event, existing) > 0);
      return index < 0
        ? [...ordered, event]
        : [...ordered.slice(0, index), event, ...ordered.slice(index)];
    }, [])
    .slice(0, limit);

const mergeDashboardEventState = <Event extends LiveEvent>(
  current: AsyncData<Event[]>,
  incoming: Event[],
  limit: number
): AsyncData<Event[]> => ({
  status: 'success',
  data: mergeDashboardEvents(current.status === 'success' ? current.data : [], incoming, limit)
});

const toRecentHistoryEvent = (event: LiveEvent): HistoryEvent => ({
  ...event,
  accessProvider: 'KISI',
  doorOpenedTimeUtc: event.doorOpenedTime
});

const prependRecentEvent = (
  events: HistoryEvent[],
  event: HistoryEvent,
  limit: RecentEventLimit
): HistoryEvent[] =>
  [event, ...events.filter((existingEvent) => existingEvent.id !== event.id)].slice(0, limit);

const parseDashboardEvent = (event: EmployeeDashboardEvent): HistoryEvent => ({
  id: event.id,
  providerEventId: event.providerEventId || undefined,
  accessProvider: event.accessProvider,
  siteId: event.siteId,
  siteLocationId: event.siteLocationId,
  doorId: event.doorId,
  doorName: event.doorName || '',
  memberEmail: event.memberEmail,
  doorOpenedTime: event.doorOpenedTime,
  doorOpenedTimeUtc: event.doorOpenedTimeUtc,
  ...parseEventMember(event),
  businessName: '',
  accessGranted: event.accessGranted,
  accessFailureCode: event.accessFailureCode || undefined,
  accessFailureMessage:
    event.accessProvider === 'GYMKEY' ? undefined : event.accessFailureMessage || undefined
});

const parseDashboardEvents = (
  events: Array<EmployeeDashboardEvent | null> | null | undefined
): HistoryEvent[] => compactValues(events).map(parseDashboardEvent);

const parseHistoryEvents = (
  response: GetEmployeeDashboardEventHistoryQuery['getEmployeeDashboardEventHistory']
): HistoryEvent[] => parseDashboardEvents(response?.items);

const parseMemberships = (
  memberships: GetEmployeeDashboardMembershipsQuery['getEmployeeDashboardMemberships']
): ActiveMembership[] =>
  compactValues(memberships).map((membership) => ({
    id: membership.memberLocationMembershipId || 0,
    name: membership.membership || '',
    validFrom: membership.validFrom || '',
    validTo: membership.validTo || '',
    scheduleType: membership.scheduleType || '',
    remainingSessions: membership.remainingSessions ?? undefined,
    remainingVisits: membership.remainingVisits ?? undefined,
    siteLocationId: membership.siteLocationId || 0,
    locationName: membership.siteLocationName || '',
    isProbableAccess: membership.isProbableAccess,
    accessProvider: membership.accessProvider || undefined
  }));

const parseUtcAppointmentDateTime = (dateTime: string | null | undefined): string =>
  dateTime ? dayjs.utc(dateTime).toISOString() : '';

const parseUpcomingAppointments = (
  bookings: GetMemberBookingDetailsQuery['getMemberBookingDetails'],
  siteLocationId: number
): DashboardBooking[] =>
  compactValues(bookings)
    .map((booking) => ({
      booking,
      startDateTime: parseUtcAppointmentDateTime(booking.startDateTime),
      endDateTime: parseUtcAppointmentDateTime(booking.endDateTime)
    }))
    .filter(
      ({ booking, startDateTime }) =>
        !booking.isDeleted &&
        booking.appointmentId !== null &&
        booking.appointmentId !== undefined &&
        Boolean(startDateTime) &&
        dayjs(startDateTime).valueOf() >= dayjs().valueOf() &&
        (booking.siteLocation || '').endsWith(`(${siteLocationId})`)
    )
    .sort(
      (first, second) =>
        dayjs(first.startDateTime).valueOf() - dayjs(second.startDateTime).valueOf()
    )
    .map(({ booking, startDateTime, endDateTime }) => ({
      id: `${booking.appointmentId}-${startDateTime}`,
      name: booking.booking || '',
      startDateTime,
      endDateTime,
      membershipName: booking.membershipName || '',
      grantsAccess: false
    }));

const parseGymKeyBookings = (
  bookings: GetMemberBookingDetailsQuery['getMemberBookingDetails']
): DashboardBooking[] =>
  compactValues(bookings).map((booking, index) => ({
    id: `${booking.importedId || booking.appointmentId || 'booking'}-${index}`,
    name: booking.booking || '',
    startDateTime: booking.startDateTime || '',
    endDateTime: booking.endDateTime || '',
    membershipName: booking.membershipName || '',
    grantsAccess: booking.grantsAccess === true
  }));

const loadMemberDashboardData = async (
  event: LiveEvent,
  siteLocationId: number,
  isGymKey: boolean
): Promise<MemberDashboardData> => {
  const [membershipResponse, bookingResponse] = await Promise.all([
    API.graphql({
      query: getEmployeeDashboardMemberships,
      variables: {
        memberId: event.memberId,
        eventId: isGymKey ? undefined : event.id,
        memberEventId: isGymKey ? event.id : undefined
      }
    }) as Promise<{ data: GetEmployeeDashboardMembershipsQuery }>,
    API.graphql({
      query: getMemberBookingDetails,
      variables: {
        memberId: event.memberId,
        memberEventId: isGymKey ? event.id : undefined
      }
    }) as Promise<{ data: GetMemberBookingDetailsQuery }>
  ]);
  const memberships = parseMemberships(membershipResponse.data.getEmployeeDashboardMemberships);
  const bookings = bookingResponse.data.getMemberBookingDetails;
  return {
    memberships: isGymKey
      ? memberships
      : memberships.filter((membership) => membership.siteLocationId === siteLocationId),
    appointments: isGymKey
      ? parseGymKeyBookings(bookings)
      : parseUpcomingAppointments(bookings, siteLocationId)
  };
};

const parseMember = (member: SiteLocationMember): Member => ({
  id: member.memberId || 0,
  uniqueId: member.uniqueId || undefined,
  clientId: member.clientId || undefined,
  accountBalance: member.accountBalance || 0,
  businessName: member.businessName || undefined,
  memberWaiverStatus: parseMemberWaiverStatus(member.memberWaiverStatus),
  memberWaiverAgreementDateTime: member.memberWaiverAgreementDateTime || undefined,
  memberWaiverGraceUntil: member.memberWaiverGraceUntil || undefined,
  memberFirstName: member.memberFirstName || '',
  memberLastName: member.memberLastName || '',
  memberFullName: `${member.memberFirstName || ''} ${member.memberLastName || ''}`.trim(),
  accessTypes: {
    isMobileAppDisabled: member.isMobileAppDisabled ?? true,
    isJustTapRevoked: member.isJustTapRevoked ?? true
  },
  signedupDate: member.signedUpDate ? dayjs(member.signedUpDate).format('DD MMM YYYY') : '',
  status: member.status || '',
  phoneNumber: member.phoneNumber || '',
  isBlocked: member.isBlocked || false,
  createdBy: member.createdBy || 0,
  createdDate: member.createdDate || '',
  lastModifiedBy: member.lastModifiedBy || 0,
  lastModifiedDate: member.lastModifiedDate || '',
  imageUrl: member.profilePicturePath || '',
  email: member.email || undefined,
  defaultLocationDetectionMethod: member.defaultGymDetectionMethod || undefined,
  lastSyncDate: member.lastSyncDate || undefined,
  isTrial: member.isTrial || undefined,
  arxReferenceId: member.arxReferenceId || undefined,
  userId: member.userId || 0,
  linkedPhoneNumber: member.linkedPhoneNumber || '',
  hasAccessCard: member.hasAccessCard || false,
  hasActiveWalletPass: member.hasActiveWalletPass ?? false,
  hasForcedAccess: member.hasForcedAccess || false,
  hasLocationOverride: member.hasLocationOverride || false,
  hasManualAccess: member.hasManualAccess || false,
  isFreepassMember: member.isFreepassMember || false,
  memberLocationId: member.memberLocationId || 0,
  language: member.language || 'en',
  displayIsImported: member.uniqueId ? 'True' : 'False',
  flag: member.flag ?? 2
});

const parseLinkCandidates = (
  response: GetMembersForSiteLocationQuery['getMembersForSiteLocation']
): LinkCandidate[] =>
  compactValues(response?.items).map((member) => ({
    id: member.memberId || 0,
    name: `${member.memberFirstName || ''} ${member.memberLastName || ''}`.trim(),
    uniqueId: member.uniqueId || '',
    clientId: member.clientId || '',
    email: member.email || ''
  }));

const formatDate = (date: string) => (date ? dayjs(date).format('D MMM YYYY') : '—');
const formatEventTime = (date: string) => (date ? dayjs(date).format('h:mm:ss A') : '—');
const formatEventDate = (date: string) => (date ? dayjs(date).format('ddd, MMM D, YYYY') : '—');
const formatWaiverDateTime = (date: string) =>
  date ? dayjs.utc(date).local().format('D MMM YYYY, h:mm A') : '';

const reportDashboardError = (
  operation: string,
  error: unknown,
  siteLocationId: number,
  doorId: number
): void => {
  const errorName = error instanceof Error ? error.name : 'GraphQLResponseError';
  console.error(operation, {
    siteLocationId,
    doorId,
    errorName
  });
};

const playDashboardAlert = async (
  audioContext: AudioContext,
  accessGranted: boolean
): Promise<void> => {
  if (audioContext.state === 'suspended') await audioContext.resume();

  const startsAt = audioContext.currentTime;
  const tones = accessGranted
    ? [{ offset: 0, duration: 0.4, frequency: 880 }]
    : [
        { offset: 0, duration: 0.18, frequency: 440 },
        { offset: 0.25, duration: 0.18, frequency: 440 }
      ];

  tones.forEach(({ offset, duration, frequency }) => {
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    const toneStartsAt = startsAt + offset;
    const toneEndsAt = toneStartsAt + duration;

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, toneStartsAt);
    gain.gain.setValueAtTime(0.0001, toneStartsAt);
    gain.gain.exponentialRampToValueAtTime(0.2, toneStartsAt + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, toneEndsAt);
    oscillator.connect(gain);
    gain.connect(audioContext.destination);
    oscillator.start(toneStartsAt);
    oscillator.stop(toneEndsAt);
  });
};

const DashboardOverlay = ({
  title,
  onClose,
  children,
  wide = false
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) => (
  <div
    className={styles.overlayBackdrop}
    role="presentation"
    onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
    <section
      className={`${styles.overlayPanel} ${wide ? styles.overlayPanelWide : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={title}>
      <header className={styles.overlayHeader}>
        <h2>{title}</h2>
        <button type="button" className={styles.iconButton} onClick={onClose} aria-label="Close">
          <span className="pi pi-times" aria-hidden="true" />
        </button>
      </header>
      <div className={styles.overlayBody}>{children}</div>
    </section>
  </div>
);

const EmptyState = ({ icon, children }: { icon: string; children: React.ReactNode }) => (
  <div className={styles.emptyState}>
    <span className={`pi ${icon}`} aria-hidden="true" />
    <span>{children}</span>
  </div>
);

const MainColumn = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const fullScreenHandle = useFullScreenHandle();
  const dispatch = useAppDispatch();
  const { gymId, chainId, doorId } = useParams<{
    gymId: string;
    chainId: string;
    doorId: string;
  }>();
  const numericSiteLocationId = Number(gymId);
  const numericGymChainId = Number(chainId);
  const numericDoorId = Number(doorId);
  const { userRole, userId } = useAppSelector((state) => state.user);
  const gyms = useAppSelector((state) => state.gyms.items);
  const hardwareProvider = useAppSelector((state) => {
    const currentChain = state.gymChain.currentGymChain;
    const chain =
      currentChain?.id === numericGymChainId
        ? currentChain
        : state.gymChain.items.find((item) => item.id === numericGymChainId);
    return chain?.hardwareProvider;
  });
  const isGymKey = hardwareProvider === HardwareProviderValue.gymkey;
  const configuredDoorName = useAppSelector((state) =>
    isGymKey
      ? state.accessPoint.items
          .find((doorGroup) => doorGroup.siteLocationId === numericSiteLocationId)
          ?.items.find((door) => door.id === numericDoorId)?.doorName
      : state.kisiDoor.items
          .find((doorGroup) => doorGroup.siteLocationId === numericSiteLocationId)
          ?.items.find((door) => door.id === numericDoorId)?.kisiDoorName
  );
  const [gym, setGym] = useState<GymType>();
  const [logoFailed, setLogoFailed] = useState(false);
  const [timeString, setTimeString] = useState(getTimeString());
  const [fullScreenLayout, setFullScreenLayout] = useState<FullScreenLayout>('withHistory');
  const [liveEvents, setLiveEvents] = useState<AsyncData<LiveEvent[]>>({ status: 'idle' });
  const [recentEvents, setRecentEvents] = useState<AsyncData<HistoryEvent[]>>({ status: 'idle' });
  const [recentEventLimit, setRecentEventLimit] = useState<RecentEventLimit>(
    DEFAULT_RECENT_EVENT_LIMIT
  );
  const [memberData, setMemberData] = useState<AsyncData<MemberDashboardData>>({ status: 'idle' });
  const [memberDataRefreshToken, setMemberDataRefreshToken] = useState(0);
  const [overlay, setOverlay] = useState<OverlayState>({ kind: 'none' });
  const [historyEvents, setHistoryEvents] = useState<AsyncData<HistoryEvent[]>>({ status: 'idle' });
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyFilters, setHistoryFilters] = useState<HistoryFilters>(emptyHistoryFilters);
  const [historyCursorStack, setHistoryCursorStack] = useState<
    Array<EmployeeDashboardEventCursorInput | undefined>
  >([undefined]);
  const [historyPageIndex, setHistoryPageIndex] = useState(0);
  const [newHistoryEventCount, setNewHistoryEventCount] = useState(0);
  const [profileMember, setProfileMember] = useState<AsyncData<Member>>({ status: 'idle' });
  const [profileRefreshToken, setProfileRefreshToken] = useState(0);
  const [linkCandidates, setLinkCandidates] = useState<AsyncData<LinkCandidate[]>>({
    status: 'idle'
  });
  const [linkingMemberId, setLinkingMemberId] = useState<number>();
  const [gymKeyAlert, setGymKeyAlert] = useState<
    { kind: 'pending' } | { kind: 'ready'; event?: LiveEvent }
  >({ kind: 'pending' });
  const currentEventIdRef = useRef<number>();
  const liveEventsRequestIdRef = useRef(0);
  const recentEventsRequestIdRef = useRef(0);
  const dashboardAudioContextRef = useRef<AudioContext>();

  useEffect(() => {
    currentEventIdRef.current = undefined;
    setGymKeyAlert({ kind: 'pending' });
    setLiveEvents({ status: 'idle' });
    setRecentEvents({ status: 'idle' });
  }, [numericDoorId, numericSiteLocationId]);

  const selectedEvent = useMemo(
    () =>
      liveEvents.status === 'success'
        ? getNewestLiveEvent(
            liveEvents.data.filter(
              (event) =>
                event.doorId === numericDoorId && event.siteLocationId === numericSiteLocationId
            ),
            isGymKey ? compareGymKeyEventRecency : compareLiveEventRecency
          )
        : undefined,
    [isGymKey, liveEvents, numericDoorId, numericSiteLocationId]
  );

  useEffect(() => {
    dispatch(getGymChainById(numericGymChainId));
    dispatch(
      fetchGyms({
        id: numericGymChainId,
        pagination: {
          offset: 0,
          pageSize: 0,
          sortField: 'siteId',
          sortOrder: 1,
          userId: userRole === UserRole.SYSTEM_ADMIN ? 0 : userId
        }
      })
    );
  }, [dispatch, numericGymChainId, numericSiteLocationId, userId, userRole]);

  useEffect(() => {
    if (hardwareProvider === undefined) return;
    if (isGymKey) dispatch(fetchAccessPoints({ siteLocationId: numericSiteLocationId }));
    else dispatch(fetchKisiDoors({ siteLocationId: numericSiteLocationId }));
  }, [dispatch, hardwareProvider, isGymKey, numericSiteLocationId]);

  useEffect(() => {
    const matchingGym = gyms
      .flatMap((gymGroup) => gymGroup.items)
      .find((candidate) => candidate.gymId === numericSiteLocationId);
    setGym(matchingGym);
    setLogoFailed(false);
  }, [gyms, numericSiteLocationId]);

  useEffect(() => {
    const interval = window.setInterval(() => setTimeString(getTimeString()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(
    () => () => {
      const audioContext = dashboardAudioContextRef.current;
      if (audioContext && audioContext.state !== 'closed') void audioContext.close();
    },
    []
  );

  const getDashboardAudioContext = useCallback(() => {
    const existingContext = dashboardAudioContextRef.current;
    if (existingContext && existingContext.state !== 'closed') return existingContext;
    const audioContext = new AudioContext();
    dashboardAudioContextRef.current = audioContext;
    return audioContext;
  }, []);

  const enterFullScreen = (layout: FullScreenLayout) => {
    setFullScreenLayout(layout);
    const audioContext = getDashboardAudioContext();
    if (audioContext.state === 'suspended') {
      void audioContext
        .resume()
        .catch((error) =>
          reportDashboardError(
            'Unable to enable employee dashboard audio',
            error,
            numericSiteLocationId,
            numericDoorId
          )
        );
    }
    void fullScreenHandle.enter();
  };

  const loadLiveEvents = useCallback(
    async (background = false, isCurrent = () => true) => {
      const requestId = liveEventsRequestIdRef.current + 1;
      liveEventsRequestIdRef.current = requestId;
      let timeout: number | undefined;
      if (!background)
        setLiveEvents((current) =>
          current.status === 'success' ? current : { status: 'loading' }
        );
      try {
        const request = API.graphql({
          query: getKisiEventsForSiteLocation,
          variables: { siteLocationId: numericSiteLocationId }
        }) as Promise<{ data: GetKisiEventsForSiteLocationQuery }>;
        if (background)
          timeout = window.setTimeout(
            () => API.cancel(request, 'Dashboard recovery timed out'),
            20000
          );
        const response = await request;
        if (!isCurrent() || requestId !== liveEventsRequestIdRef.current) return false;
        const events = compactValues(response.data.getKisiEventsForSiteLocation?.items).map(
          parseKisiLiveEvent
        );
        const newestDoorEvent = getNewestLiveEvent(
          events.filter((event) => event.doorId === numericDoorId)
        );
        if (newestDoorEvent) {
          currentEventIdRef.current = Math.max(currentEventIdRef.current || 0, newestDoorEvent.id);
        }
        setLiveEvents((current) => {
          const displayedEvent =
            current.status === 'success'
              ? getNewestLiveEvent(
                  current.data.filter(
                    (event) =>
                      event.siteLocationId === numericSiteLocationId &&
                      event.doorId === numericDoorId
                  )
                )
              : undefined;
          return displayedEvent &&
            (!newestDoorEvent || compareLiveEventRecency(displayedEvent, newestDoorEvent) > 0)
            ? current
            : { status: 'success', data: events };
        });
        return true;
      } catch (error) {
        if (!isCurrent() || requestId !== liveEventsRequestIdRef.current) return false;
        reportDashboardError(
          'Unable to load employee dashboard events',
          error,
          numericSiteLocationId,
          numericDoorId
        );
        if (!background) setLiveEvents({ status: 'error' });
        return false;
      } finally {
        window.clearTimeout(timeout);
      }
    },
    [numericDoorId, numericSiteLocationId]
  );

  const loadRecentEvents = useCallback(
    async (background = false, isCurrent = () => true) => {
      const requestId = recentEventsRequestIdRef.current + 1;
      recentEventsRequestIdRef.current = requestId;
      let timeout: number | undefined;
      if (!background) {
        setRecentEvents((current) =>
          current.status === 'success' ? current : { status: 'loading' }
        );
        if (isGymKey) {
          setLiveEvents((current) =>
            current.status === 'success' ? current : { status: 'loading' }
          );
        }
      }
      try {
        const input: EmployeeDashboardEventHistoryInput = {
          siteLocationId: numericSiteLocationId,
          doorId: numericDoorId,
          pageSize: recentEventLimit
        };
        const request = API.graphql({
          query: getEmployeeDashboardEventHistory,
          variables: { input }
        }) as Promise<{ data: GetEmployeeDashboardEventHistoryQuery }>;
        if (background)
          timeout = window.setTimeout(
            () => API.cancel(request, 'Dashboard recovery timed out'),
            20000
          );
        const response = await request;
        if (!isCurrent() || requestId !== recentEventsRequestIdRef.current) return false;
        const events = parseHistoryEvents(response.data.getEmployeeDashboardEventHistory);
        setRecentEvents((current) =>
          isGymKey
            ? mergeDashboardEventState(current, events, recentEventLimit)
            : { status: 'success', data: events }
        );
        if (isGymKey) {
          setGymKeyAlert((current) =>
            current.kind === 'pending'
              ? { kind: 'ready', event: getNewestLiveEvent(events, compareGymKeyEventRecency) }
              : current
          );
          setLiveEvents((current) => mergeDashboardEventState(current, events, recentEventLimit));
        }
        return true;
      } catch (error) {
        if (!isCurrent() || requestId !== recentEventsRequestIdRef.current) return false;
        reportDashboardError(
          'Unable to load recent door history',
          error,
          numericSiteLocationId,
          numericDoorId
        );
        if (!background) {
          setRecentEvents((current) =>
            current.status === 'success' ? current : { status: 'error' }
          );
          if (isGymKey) {
            setGymKeyAlert((current) => (current.kind === 'pending' ? { kind: 'ready' } : current));
            setLiveEvents((current) =>
              current.status === 'success' ? current : { status: 'error' }
            );
          }
        }
        return false;
      } finally {
        window.clearTimeout(timeout);
      }
    },
    [isGymKey, numericDoorId, numericSiteLocationId, recentEventLimit]
  );

  useEffect(() => {
    if (hardwareProvider === undefined || isGymKey) return;
    void Promise.all([loadLiveEvents(), loadRecentEvents()]);
  }, [hardwareProvider, isGymKey, loadLiveEvents, loadRecentEvents]);

  useEffect(() => {
    if (!isGymKey) return;
    let active = true;
    void loadRecentEvents(false, () => active);
    return () => {
      active = false;
      recentEventsRequestIdRef.current += 1;
    };
  }, [isGymKey, loadRecentEvents]);

  useEffect(() => {
    if (!selectedEvent?.memberId) {
      setMemberData({ status: 'idle' });
      return;
    }

    let isCurrent = true;
    setMemberData({ status: 'loading' });
    const loadMemberData = async () => {
      try {
        const data = await loadMemberDashboardData(selectedEvent, numericSiteLocationId, isGymKey);
        if (isCurrent) setMemberData({ status: 'success', data });
      } catch (error) {
        reportDashboardError(
          'Unable to load dashboard member data',
          error,
          numericSiteLocationId,
          numericDoorId
        );
        if (isCurrent) setMemberData({ status: 'error' });
      }
    };
    void loadMemberData();
    return () => {
      isCurrent = false;
    };
  }, [
    isGymKey,
    memberDataRefreshToken,
    numericDoorId,
    numericSiteLocationId,
    selectedEvent?.id,
    selectedEvent?.memberId
  ]);

  const loadHistory = useCallback(
    async (cursor?: EmployeeDashboardEventCursorInput) => {
      setHistoryEvents({ status: 'loading' });
      try {
        const input: EmployeeDashboardEventHistoryInput = {
          siteLocationId: numericSiteLocationId,
          doorId: numericDoorId,
          pageSize: HISTORY_PAGE_SIZE,
          cursor,
          dateFrom: historyFilters.dateFrom || undefined,
          dateTo: historyFilters.dateTo || undefined,
          memberId: historyFilters.memberId,
          accessGranted:
            historyFilters.accessResult === 'all'
              ? undefined
              : historyFilters.accessResult === 'granted',
          search: historyFilters.search.trim() || undefined
        };
        const response = (await API.graphql({
          query: getEmployeeDashboardEventHistory,
          variables: { input }
        })) as { data: GetEmployeeDashboardEventHistoryQuery };
        setHistoryEvents({
          status: 'success',
          data: parseHistoryEvents(response.data.getEmployeeDashboardEventHistory)
        });
        setHistoryTotal(
          response.data.getEmployeeDashboardEventHistory?.totalRecords?.totalRecords || 0
        );
      } catch (error) {
        reportDashboardError(
          'Unable to load full door history',
          error,
          numericSiteLocationId,
          numericDoorId
        );
        setHistoryEvents({ status: 'error' });
      }
    },
    [historyFilters, numericDoorId, numericSiteLocationId]
  );

  useEffect(() => {
    if (overlay.kind !== 'history') return;
    const timeout = window.setTimeout(() => {
      setHistoryCursorStack([undefined]);
      setHistoryPageIndex(0);
      setNewHistoryEventCount(0);
      void loadHistory();
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [historyFilters, loadHistory, overlay.kind]);

  useEffect(() => {
    if (overlay.kind !== 'profile') {
      setProfileMember({ status: 'idle' });
      return;
    }
    let isCurrent = true;
    setProfileMember((current) => (current.status === 'success' ? current : { status: 'loading' }));
    const loadProfile = async () => {
      try {
        const response = (await API.graphql({
          query: getMemberByMemberId,
          variables: {
            siteLocationId: numericSiteLocationId,
            memberId: overlay.memberId,
            offset: 0,
            pageSize: 1,
            sortField: 'memberFirstName',
            sortOrder: 'ASC',
            search: { searchField: '', searchText: '' }
          }
        })) as { data: GetMemberByMemberIdQuery };
        const member = compactValues(response.data.getMemberByMemberId?.items)[0];
        if (member) {
          if (isCurrent) setProfileMember({ status: 'success', data: parseMember(member) });
          return;
        }
      } catch (error) {
        reportDashboardError(
          'Unable to load the site member profile',
          error,
          numericSiteLocationId,
          numericDoorId
        );
      }
      if (isCurrent) setProfileMember({ status: 'error' });
    };
    void loadProfile();
    return () => {
      isCurrent = false;
    };
  }, [numericDoorId, numericSiteLocationId, overlay, profileRefreshToken]);

  useEffect(() => {
    if (overlay.kind !== 'link') {
      setLinkCandidates({ status: 'idle' });
      return;
    }
    let isCurrent = true;
    setLinkCandidates({ status: 'loading' });
    const loadCandidates = async () => {
      try {
        const response = (await API.graphql({
          query: getMembersForSiteLocation,
          variables: {
            siteLocationId: numericSiteLocationId,
            offset: 0,
            pageSize: 25,
            sortField: 'createdDate',
            sortOrder: 'DESC',
            search: { searchField: 'email', searchText: overlay.event.memberEmail }
          }
        })) as { data: GetMembersForSiteLocationQuery };
        if (isCurrent) {
          setLinkCandidates({
            status: 'success',
            data: parseLinkCandidates(response.data.getMembersForSiteLocation).filter(
              (member) =>
                member.email.toLocaleLowerCase() === overlay.event.memberEmail.toLocaleLowerCase()
            )
          });
        }
      } catch (error) {
        reportDashboardError(
          'Unable to load members for event linking',
          error,
          numericSiteLocationId,
          numericDoorId
        );
        if (isCurrent) setLinkCandidates({ status: 'error' });
      }
    };
    void loadCandidates();
    return () => {
      isCurrent = false;
    };
  }, [numericDoorId, numericSiteLocationId, overlay]);

  useEmployeeDashboardSubscription<KisiEvent>(
    numericSiteLocationId,
    numericDoorId,
    {
      onEvents: (events) => {
        const nextEvents = compactValues(events).map(parseKisiLiveEvent);
        const triggeringEvent = getNewestLiveEvent(nextEvents);
        if (
          !triggeringEvent ||
          triggeringEvent.siteLocationId !== numericSiteLocationId ||
          triggeringEvent.doorId !== numericDoorId
        )
          return;
        const newestDoorEvent = getNewestLiveEvent(
          nextEvents.filter((event) => event.doorId === numericDoorId)
        );
        if (!newestDoorEvent || newestDoorEvent.id !== triggeringEvent.id) return;
        if (
          currentEventIdRef.current !== undefined &&
          newestDoorEvent.id < currentEventIdRef.current
        )
          return;

        setLiveEvents({ status: 'success', data: nextEvents });
        setRecentEvents((current) => ({
          status: 'success',
          data: prependRecentEvent(
            current.status === 'success' ? current.data : [],
            toRecentHistoryEvent(newestDoorEvent),
            recentEventLimit
          )
        }));
        if (newestDoorEvent?.memberId) {
          setMemberDataRefreshToken((token) => token + 1);
        }
        void loadRecentEvents();
        if (newestDoorEvent.id !== currentEventIdRef.current) {
          currentEventIdRef.current = newestDoorEvent.id;
          void playDashboardAlert(
            getDashboardAudioContext(),
            newestDoorEvent.accessGranted
          ).catch((error) =>
            reportDashboardError(
              'Unable to play employee dashboard notification',
              error,
              numericSiteLocationId,
              numericDoorId
            )
          );
          if (overlay.kind === 'history') {
            if (historyPageIndex === 0) void loadHistory();
            else setNewHistoryEventCount((count) => count + 1);
          }
        }
      },
      onRecovered: async (isCurrent) => {
        const results = await Promise.all([
          loadLiveEvents(true, isCurrent),
          loadRecentEvents(true, isCurrent)
        ]);
        if (!isCurrent()) return false;
        if (results.every(Boolean)) {
          if (overlay.kind === 'history' && historyPageIndex === 0) void loadHistory();
          return true;
        }
        return false;
      }
    },
    !isGymKey
  );

  useEmployeeDashboardSubscription<EmployeeDashboardEvent>(
    numericSiteLocationId,
    numericDoorId,
    {
      onEvents: (events) => {
        const doorEvents = parseDashboardEvents(events).filter(
          (event) =>
            event.siteLocationId === numericSiteLocationId && event.doorId === numericDoorId
        );
        if (doorEvents.length === 0) return;
        setRecentEvents((current) =>
          mergeDashboardEventState(current, doorEvents, recentEventLimit)
        );
        setLiveEvents((current) => mergeDashboardEventState(current, doorEvents, recentEventLimit));
      },
      onRecovered: async (isCurrent) => {
        const recovered = await loadRecentEvents(true, isCurrent);
        if (recovered && overlay.kind === 'history' && historyPageIndex === 0) void loadHistory();
        return recovered;
      }
    },
    isGymKey,
    {
      query: onGymKeyEmployeeDashboardUpdate,
      fieldName: 'onGymKeyEmployeeDashboardUpdate'
    }
  );

  useEffect(() => {
    if (!isGymKey || !selectedEvent || gymKeyAlert.kind === 'pending') return;
    const previousEvent = gymKeyAlert.event;
    if (previousEvent && compareGymKeyEventRecency(selectedEvent, previousEvent) <= 0) return;

    setGymKeyAlert({ kind: 'ready', event: selectedEvent });
    void playDashboardAlert(
      getDashboardAudioContext(),
      selectedEvent.accessGranted
    ).catch((error) =>
      reportDashboardError(
        'Unable to play employee dashboard notification',
        error,
        numericSiteLocationId,
        numericDoorId
      )
    );
    if (overlay.kind === 'history') {
      if (historyPageIndex === 0) void loadHistory();
      else {
        const newEventCount =
          recentEvents.status === 'success'
            ? recentEvents.data.filter(
                (event) => !previousEvent || compareGymKeyEventRecency(event, previousEvent) > 0
              ).length
            : 1;
        setNewHistoryEventCount((count) => count + newEventCount);
      }
    }
  }, [
    getDashboardAudioContext,
    gymKeyAlert,
    historyPageIndex,
    isGymKey,
    loadHistory,
    numericDoorId,
    numericSiteLocationId,
    overlay.kind,
    recentEvents,
    selectedEvent
  ]);

  const openProfile = (memberId?: number) => {
    if (memberId) setOverlay({ kind: 'profile', memberId });
  };

  const linkMember = async (candidate: LinkCandidate, event: HistoryEvent | LiveEvent) => {
    setLinkingMemberId(candidate.id);
    try {
      const response = (await API.graphql({
        query: linkMemberToEvent,
        variables: { memberId: candidate.id, eventId: event.id }
      })) as { data: { linkMemberToEvent?: number | null } };
      if (!response.data.linkMemberToEvent) {
        throw Error('The event was not updated');
      }
      setOverlay({ kind: 'none' });
      await Promise.all([loadLiveEvents(), loadRecentEvents()]);
      if (historyEvents.status === 'success') {
        await loadHistory(historyCursorStack[historyPageIndex]);
      }
    } catch (error) {
      reportDashboardError(
        'Unable to link member to door event',
        error,
        numericSiteLocationId,
        numericDoorId
      );
      setLinkCandidates({ status: 'error' });
    } finally {
      setLinkingMemberId(undefined);
    }
  };

  const historyItems = historyEvents.status === 'success' ? historyEvents.data : [];
  const recentItems =
    recentEvents.status === 'success' ? recentEvents.data.slice(0, recentEventLimit) : [];
  const selectedMemberId =
    selectedEvent?.memberId ||
    (selectedEvent
      ? recentItems.find((event) => event.id === selectedEvent.id)?.memberId
      : undefined);
  const dashboardPanelItemLimit =
    fullScreenHandle.active && fullScreenLayout === 'liveCheckIn'
      ? LIVE_CHECK_IN_PANEL_ITEM_LIMIT
      : DASHBOARD_PANEL_ITEM_LIMIT;
  const memberOptions = useMemo(() => {
    const namesById = [...recentItems, ...historyItems].reduce<Record<number, string>>(
      (names, event) =>
        event.memberId && event.memberName
          ? { ...names, [event.memberId]: event.memberName }
          : names,
      {}
    );
    return Object.entries(namesById)
      .map(([id, name]) => ({ id: Number(id), name }))
      .sort((first, second) => first.name.localeCompare(second.name));
  }, [historyItems, recentItems]);

  const goToNextHistoryPage = () => {
    const lastEvent = historyItems[historyItems.length - 1];
    if (!lastEvent) return;
    const cursor = { doorOpenedTimeUtc: lastEvent.doorOpenedTimeUtc, id: lastEvent.id };
    const nextIndex = historyPageIndex + 1;
    setHistoryCursorStack((stack) => [...stack.slice(0, nextIndex), cursor]);
    setHistoryPageIndex(nextIndex);
    void loadHistory(cursor);
  };

  const goToPreviousHistoryPage = () => {
    const previousIndex = Math.max(0, historyPageIndex - 1);
    setHistoryPageIndex(previousIndex);
    void loadHistory(historyCursorStack[previousIndex]);
  };

  const showNewestHistory = () => {
    setHistoryCursorStack([undefined]);
    setHistoryPageIndex(0);
    setNewHistoryEventCount(0);
    void loadHistory();
  };

  const profileButton = selectedMemberId ? (
    <button
      type="button"
      className={`${styles.primaryButton} ${styles.profileButton}`}
      onClick={() => openProfile(selectedMemberId)}>
      <span className="pi pi-user" aria-hidden="true" />
      {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.OPEN_PROFILE')}
    </button>
  ) : null;

  return (
    <div className={styles.pageShell}>
      <FullScreen handle={fullScreenHandle} className={styles.fullScreen}>
        <div
          className={`${styles.dashboard} ${
            fullScreenHandle.active ? styles.dashboardFullScreen : ''
          }`}>
          <header className={styles.gymHeader}>
            <div className={styles.brandLockup}>
              {!fullScreenHandle.active && (
                <button
                  type="button"
                  className={styles.headerBackButton}
                  onClick={() => history.push(`/gymChains/${chainId}/gyms/${gymId}/liveAccess`)}>
                  <span className="pi pi-arrow-left" aria-hidden="true" />
                  {t('EMPLOYEE_DASHBOARD_PAGE.BACK_TO_DOORS')}
                </button>
              )}
              <div className={styles.logoPlate}>
                {gym?.imageUrl && !logoFailed ? (
                  <img
                    src={gym.imageUrl}
                    className={styles.gymLogo}
                    alt={`${gym.gymName} logo`}
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <div className={styles.fallbackLogo} aria-hidden="true">
                    {(gym?.gymName || 'GX').slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.headerTitle}>
              <p className={styles.gymName}>
                {gym?.gymName || t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.GYM_FALLBACK')}
              </p>
              <p className={styles.doorName}>
                {configuredDoorName ||
                  selectedEvent?.doorName ||
                  recentItems[0]?.doorName ||
                  t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.DOOR_FALLBACK')}
              </p>
            </div>
            <div className={styles.headerActions}>
              <span className={styles.clock}>{timeString}</span>
              {!fullScreenHandle.active && (
                <div
                  className={styles.fullScreenOptions}
                  role="group"
                  aria-label={t('EMPLOYEE_DASHBOARD_PAGE.FULL_SCREEN_OPTIONS')}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => enterFullScreen('liveCheckIn')}>
                    <span className="pi pi-window-maximize" aria-hidden="true" />
                    {t('EMPLOYEE_DASHBOARD_PAGE.FULL_SCREEN_LIVE_CHECK_IN')}
                  </button>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => enterFullScreen('withHistory')}>
                    <span className="pi pi-history" aria-hidden="true" />
                    {t('EMPLOYEE_DASHBOARD_PAGE.FULL_SCREEN_WITH_HISTORY')}
                  </button>
                </div>
              )}
            </div>
          </header>

          <main
            className={`${styles.dashboardGrid} ${
              fullScreenHandle.active && fullScreenLayout === 'liveCheckIn'
                ? styles.dashboardGridLiveOnly
                : ''
            }`}>
            <section
              className={styles.memberColumn}
              aria-label={t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LIVE_CHECK_IN')}>
              <article
                className={`${styles.card} ${styles.heroCard} ${
                  selectedEvent && !selectedEvent.accessGranted ? styles.deniedHeroCard : ''
                }`}>
                <div className={styles.liveLabel}>
                  <span className={styles.liveDot} aria-hidden="true" />
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LIVE_CHECK_IN')}
                </div>
                {selectedEvent ? (
                  <div className={styles.heroContent}>
                    <div className={styles.photoColumn}>
                      <div className={styles.photoFrame}>
                        <img
                          src={
                            toMindbodyMemberProfileImageUrl(
                              selectedEvent.businessName,
                              selectedEvent.memberUniqueId
                            ) || memberPlaceholder
                          }
                          alt=""
                          onError={({ currentTarget }) => {
                            currentTarget.onerror = null;
                            currentTarget.src = memberPlaceholder;
                          }}
                        />
                        <span className={styles.checkBadge} aria-hidden="true">
                          <span
                            className={`pi ${
                              selectedEvent.accessGranted ? 'pi-check' : 'pi-times'
                            }`}
                          />
                        </span>
                      </div>
                    </div>
                    <div className={styles.checkInDetails}>
                      <h1>{selectedEvent.memberName || selectedEvent.memberEmail || '—'}</h1>
                      <div
                        className={
                          selectedEvent.accessGranted ? styles.checkedInBadge : styles.deniedBadge
                        }>
                        <span
                          className={`pi ${
                            selectedEvent.accessGranted ? 'pi-check-circle' : 'pi-times-circle'
                          }`}
                          aria-hidden="true"
                        />
                        {selectedEvent.accessGranted
                          ? t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.CHECKED_IN')
                          : t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.ACCESS_DENIED')}
                      </div>
                      <time dateTime={selectedEvent.doorOpenedTime}>
                        <strong>{formatEventTime(selectedEvent.doorOpenedTime)}</strong>
                        <span>{formatEventDate(selectedEvent.doorOpenedTime)}</span>
                      </time>
                      {!selectedEvent.accessGranted && (
                        <div className={styles.accessFailureReason}>
                          <span>
                            {selectedEvent.accessFailureMessage ||
                              t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.ACCESS_DENIED_FALLBACK')}
                          </span>
                        </div>
                      )}
                      {selectedEvent.memberStatus && (
                        <span className={styles.memberStatus}>
                          {t('EMPLOYEE_DASHBOARD_PAGE.KISI_DASHBOARD_CAROUSEL.STATUS_LABEL')}
                          {selectedEvent.memberStatus}
                        </span>
                      )}
                      {selectedEvent.memberWaiverPolicyEnforced && (
                        <span
                          className={`${styles.waiverStatus} ${
                            selectedEvent.memberWaiverStatus === 'signed'
                              ? styles.waiverSigned
                              : selectedEvent.memberWaiverStatus === 'grace'
                              ? styles.waiverGrace
                              : selectedEvent.memberWaiverStatus === 'not-signed'
                              ? styles.waiverNotSigned
                              : styles.waiverUnknown
                          }`}>
                          <span className="pi pi-file" aria-hidden="true" />
                          {selectedEvent.memberWaiverStatus === 'signed'
                            ? selectedEvent.memberWaiverAgreementDateTime
                              ? t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.WAIVER_SIGNED_ON', {
                                  date: formatWaiverDateTime(
                                    selectedEvent.memberWaiverAgreementDateTime
                                  )
                                })
                              : t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.WAIVER_SIGNED')
                            : selectedEvent.memberWaiverStatus === 'grace'
                            ? t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.WAIVER_GRACE_UNTIL', {
                                date: formatWaiverDateTime(selectedEvent.memberWaiverGraceUntil)
                              })
                            : selectedEvent.memberWaiverStatus === 'not-signed'
                            ? t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.WAIVER_NOT_SIGNED')
                            : t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.WAIVER_UNAVAILABLE')}
                        </span>
                      )}
                      {profileButton}
                    </div>
                  </div>
                ) : liveEvents.status === 'loading' ? (
                  <EmptyState icon="pi-spin pi-spinner">
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LOADING')}
                  </EmptyState>
                ) : liveEvents.status === 'error' ? (
                  <EmptyState icon="pi-exclamation-circle">
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.HISTORY_UNAVAILABLE')}
                  </EmptyState>
                ) : (
                  <EmptyState icon="pi-sign-in">
                    {t(
                      'EMPLOYEE_DASHBOARD_PAGE.KISI_EVENTS_TABLE.NO_RECORDED_EVENTS_FOR_DOOR_LABEL'
                    )}
                  </EmptyState>
                )}
              </article>

              <div className={styles.summaryGrid}>
                <article className={`${styles.card} ${styles.summaryCard}`}>
                  <div className={styles.cardTitle}>
                    <span className="pi pi-wallet" aria-hidden="true" />
                    <h2>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.ACCOUNT_BALANCE')}</h2>
                  </div>
                  {selectedEvent?.memberId ? (
                    <>
                      <strong className={styles.balanceValue}>
                        ${selectedEvent.memberAccountBalance.toFixed(2)}
                      </strong>
                      <span className={styles.mutedText}>
                        {selectedEvent.memberAccountBalance < 0
                          ? t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.OUTSTANDING_BALANCE')
                          : t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NO_OUTSTANDING_BALANCE')}
                      </span>
                    </>
                  ) : (
                    <EmptyState icon="pi-wallet">
                      {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.BALANCE_UNAVAILABLE')}
                    </EmptyState>
                  )}
                </article>

                <article className={`${styles.card} ${styles.summaryCard}`}>
                  <div className={styles.cardTitle}>
                    <span className="pi pi-file-edit" aria-hidden="true" />
                    <h2>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NOTES')}</h2>
                  </div>
                  {selectedEvent?.memberNote ? (
                    <>
                      <p className={styles.notePreview}>{selectedEvent.memberNote}</p>
                      <button
                        type="button"
                        className={styles.textButton}
                        onClick={() =>
                          setOverlay({ kind: 'note', note: selectedEvent.memberNote })
                        }>
                        {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.VIEW_FULL_NOTE')}
                      </button>
                    </>
                  ) : (
                    <EmptyState icon="pi-file-edit">
                      {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NO_NOTES')}
                    </EmptyState>
                  )}
                </article>
              </div>

              <article className={`${styles.card} ${styles.listPanel}`}>
                <div className={styles.panelHeader}>
                  <div className={styles.cardTitle}>
                    <span className="pi pi-tag" aria-hidden="true" />
                    <h2>
                      {t(
                        isGymKey
                          ? 'GYM_CHAIN_ACCESS_CATEGORY_HEADER.MEMBERSHIPS'
                          : 'EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.ACTIVE_MEMBERSHIPS'
                      )}
                    </h2>
                  </div>
                  {selectedMemberId && (
                    <button
                      type="button"
                      className={styles.textButton}
                      onClick={() => openProfile(selectedMemberId)}>
                      {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.VIEW_ALL')}
                    </button>
                  )}
                </div>
                {memberData.status === 'loading' ? (
                  <EmptyState icon="pi-spin pi-spinner">
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LOADING')}
                  </EmptyState>
                ) : selectedEvent && !selectedEvent.memberId ? (
                  <EmptyState icon="pi-user-minus">
                    {t(
                      isGymKey
                        ? 'EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.MEMBER_UNAVAILABLE'
                        : 'EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.MEMBER_NOT_LINKED'
                    )}
                  </EmptyState>
                ) : memberData.status === 'error' ? (
                  <EmptyState icon="pi-exclamation-circle">
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.MEMBERSHIPS_UNAVAILABLE')}
                  </EmptyState>
                ) : memberData.status === 'success' && memberData.data.memberships.length > 0 ? (
                  <div className={styles.membershipList}>
                    {memberData.data.memberships
                      .slice(0, dashboardPanelItemLimit)
                      .map((membership) => (
                        <div
                          key={membership.id}
                          className={`${styles.membershipItem} ${
                            membership.isProbableAccess ? styles.probableMembership : ''
                          }`}>
                          <div>
                            <div className={styles.itemHeading}>
                              <strong>{membership.name}</strong>
                              {membership.isProbableAccess && (
                                <span className={styles.probableBadge}>
                                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.PROBABLE_ACCESS')}
                                </span>
                              )}
                            </div>
                            <span className={styles.mutedText}>
                              {formatDate(membership.validFrom)} – {formatDate(membership.validTo)}
                              {membership.locationName ? ` · ${membership.locationName}` : ''}
                            </span>
                          </div>
                          <div className={styles.membershipUsage}>
                            {membership.remainingSessions !== undefined && (
                              <span>
                                <strong>{membership.remainingSessions}</strong>{' '}
                                {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.SESSIONS_REMAINING')}
                              </span>
                            )}
                            {membership.remainingVisits !== undefined && (
                              <span>
                                <strong>{membership.remainingVisits}</strong>{' '}
                                {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.VISITS_REMAINING')}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <EmptyState icon="pi-tag">
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NO_ACTIVE_MEMBERSHIPS')}
                  </EmptyState>
                )}
              </article>

              <article className={`${styles.card} ${styles.listPanel}`}>
                <div className={styles.panelHeader}>
                  <div className={styles.cardTitle}>
                    <span className="pi pi-calendar" aria-hidden="true" />
                    <h2>
                      {t(
                        isGymKey
                          ? 'EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.BOOKINGS'
                          : 'EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.UPCOMING_APPOINTMENTS'
                      )}
                    </h2>
                  </div>
                  {selectedMemberId && (
                    <button
                      type="button"
                      className={styles.textButton}
                      onClick={() => openProfile(selectedMemberId)}>
                      {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.VIEW_ALL')}
                    </button>
                  )}
                </div>
                {memberData.status === 'loading' ? (
                  <EmptyState icon="pi-spin pi-spinner">
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LOADING')}
                  </EmptyState>
                ) : selectedEvent && !selectedEvent.memberId ? (
                  <EmptyState icon="pi-user-minus">
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.MEMBER_NOT_LINKED')}
                  </EmptyState>
                ) : memberData.status === 'error' ? (
                  <EmptyState icon="pi-exclamation-circle">
                    {t(
                      isGymKey
                        ? 'EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.BOOKINGS_UNAVAILABLE'
                        : 'EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.APPOINTMENTS_UNAVAILABLE'
                    )}
                  </EmptyState>
                ) : memberData.status === 'success' && memberData.data.appointments.length > 0 ? (
                  <div
                    className={`${styles.appointmentList} ${
                      isGymKey ? styles.currentBookings : ''
                    }`}>
                    {(isGymKey
                      ? memberData.data.appointments
                      : memberData.data.appointments.slice(0, dashboardPanelItemLimit)
                    ).map((appointment) => (
                      <div
                        key={appointment.id}
                        className={`${styles.appointmentItem} ${
                          appointment.grantsAccess
                            ? `${styles.probableMembership} ${styles.grantingBooking}`
                            : ''
                        }`}>
                        <span className="pi pi-calendar" aria-hidden="true" />
                        <div>
                          <div className={styles.itemHeading}>
                            <strong>{appointment.name}</strong>
                            {appointment.grantsAccess && (
                              <span className={styles.probableBadge}>
                                {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.PROBABLE_ACCESS')}
                              </span>
                            )}
                          </div>
                          {appointment.membershipName && <span>{appointment.membershipName}</span>}
                        </div>
                        <time dateTime={appointment.startDateTime}>
                          <strong>
                            {dayjs(appointment.startDateTime).format('ddd, MMM D, YYYY')}
                          </strong>
                          <span>
                            {dayjs(appointment.startDateTime).format('h:mm A')} –{' '}
                            {dayjs(appointment.endDateTime).format('h:mm A')}
                          </span>
                        </time>
                        {!appointment.grantsAccess && (
                          <span className={styles.upcomingBadge}>
                            {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.UPCOMING')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon="pi-calendar">
                    {t(
                      isGymKey
                        ? 'EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NO_BOOKINGS'
                        : 'EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NO_UPCOMING_APPOINTMENTS'
                    )}
                  </EmptyState>
                )}
              </article>
            </section>

            {(!fullScreenHandle.active || fullScreenLayout === 'withHistory') && (
              <aside
                className={`${styles.card} ${styles.historyPanel} ${
                  recentItems.length === recentEventLimit ? styles.historyPanelFull : ''
                }`}>
                <div className={styles.historyHeading}>
                  <div className={styles.cardTitle}>
                    <span className="pi pi-history" aria-hidden="true" />
                    <h2>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.RECENT_ENTRY_HISTORY')}</h2>
                  </div>
                  <div className={styles.historyActions}>
                    <label className={styles.historyLimitSelector}>
                      <span>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.SHOW')}</span>
                      <select
                        aria-label={t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.EVENTS_SHOWN')}
                        value={recentEventLimit}
                        onInput={({ currentTarget }) =>
                          setRecentEventLimit(parseRecentEventLimit(currentTarget.value))
                        }>
                        {RECENT_EVENT_LIMIT_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className={styles.textButton}
                      onClick={() => setOverlay({ kind: 'history' })}>
                      {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.VIEW_FULL_HISTORY')}
                    </button>
                  </div>
                </div>
                <div className={styles.historyTableHeader} aria-hidden="true">
                  <span>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.RESULT')}</span>
                  <span>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.TIME')}</span>
                  <span>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.DOOR')}</span>
                  <span>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.MEMBER_NAME')}</span>
                </div>
                {recentEvents.status === 'loading' ? (
                  <EmptyState icon="pi-spin pi-spinner">
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LOADING')}
                  </EmptyState>
                ) : recentEvents.status === 'error' ? (
                  <EmptyState icon="pi-exclamation-circle">
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.HISTORY_UNAVAILABLE')}
                  </EmptyState>
                ) : recentItems.length > 0 ? (
                  <div
                    className={`${styles.historyRows} ${
                      recentItems.length === recentEventLimit ? styles.historyRowsFull : ''
                    } ${recentEventLimit === 15 ? styles.historyRowsCompact : ''} ${
                      recentEventLimit === 20 ? styles.historyRowsDense : ''
                    }`}
                    style={
                      recentItems.length === recentEventLimit
                        ? {
                            gridTemplateRows: `repeat(${recentEventLimit}, minmax(0, 1fr))`
                          }
                        : undefined
                    }>
                    {recentItems.map((event) => (
                      <HistoryRow
                        key={event.id}
                        event={event}
                        onOpenProfile={openProfile}
                        onLink={isGymKey ? undefined : () => setOverlay({ kind: 'link', event })}
                        t={t}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState icon="pi-history">
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NO_DOOR_EVENTS')}
                  </EmptyState>
                )}
              </aside>
            )}
          </main>

          {overlay.kind === 'note' && (
            <DashboardOverlay
              title={t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.FULL_NOTE')}
              onClose={() => setOverlay({ kind: 'none' })}>
              <p className={styles.fullNote}>{overlay.note}</p>
            </DashboardOverlay>
          )}

          {overlay.kind === 'profile' && (
            <DashboardOverlay
              title={t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.GYMAXESS_PROFILE')}
              onClose={() => setOverlay({ kind: 'none' })}
              wide>
              {profileMember.status === 'loading' ? (
                <EmptyState icon="pi-spin pi-spinner">
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LOADING_PROFILE')}
                </EmptyState>
              ) : profileMember.status === 'success' ? (
                <MemberDetailsModalContent
                  member={profileMember.data}
                  setForceResync={setProfileRefreshToken}
                  gymId={numericSiteLocationId}
                  chainId={numericGymChainId}
                  stayOnCurrentPage
                />
              ) : (
                <EmptyState icon="pi-user">
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.PROFILE_UNAVAILABLE')}
                </EmptyState>
              )}
            </DashboardOverlay>
          )}

          {overlay.kind === 'link' && (
            <DashboardOverlay
              title={t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LINK_MEMBER')}
              onClose={() => setOverlay({ kind: 'none' })}
              wide>
              <p className={styles.overlayIntro}>
                {overlay.event.memberEmail} · {overlay.event.doorName} ·{' '}
                {formatEventDate(overlay.event.doorOpenedTime)}
              </p>
              {linkCandidates.status === 'loading' ? (
                <EmptyState icon="pi-spin pi-spinner">
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LOADING')}
                </EmptyState>
              ) : linkCandidates.status === 'error' ? (
                <EmptyState icon="pi-exclamation-circle">
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LINKING_UNAVAILABLE')}
                </EmptyState>
              ) : linkCandidates.status === 'success' && linkCandidates.data.length > 0 ? (
                <div className={styles.candidateList}>
                  {linkCandidates.data.map((candidate) => (
                    <div key={candidate.id} className={styles.candidateRow}>
                      <div>
                        <strong>{candidate.name}</strong>
                        <span>{candidate.email}</span>
                      </div>
                      <span>{candidate.clientId || candidate.uniqueId || '—'}</span>
                      <button
                        type="button"
                        className={styles.linkMemberButton}
                        disabled={linkingMemberId !== undefined}
                        onClick={() => void linkMember(candidate, overlay.event)}>
                        <span
                          className={`pi ${
                            linkingMemberId === candidate.id ? 'pi-spin pi-spinner' : 'pi-link'
                          }`}
                          aria-hidden="true"
                        />
                        {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LINK_MEMBER')}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon="pi-user-minus">
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NO_LINKABLE_MEMBERS')}
                </EmptyState>
              )}
            </DashboardOverlay>
          )}

          {overlay.kind === 'history' && (
            <DashboardOverlay
              title={t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.FULL_ENTRY_HISTORY')}
              onClose={() => setOverlay({ kind: 'none' })}
              wide>
              <div className={styles.filters}>
                <label>
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.FROM_DATE')}
                  <input
                    type="date"
                    value={historyFilters.dateFrom}
                    onChange={({ currentTarget }) => {
                      const dateFrom = currentTarget.value;
                      setHistoryFilters((filters) => ({ ...filters, dateFrom }));
                    }}
                  />
                </label>
                <label>
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.TO_DATE')}
                  <input
                    type="date"
                    value={historyFilters.dateTo}
                    min={historyFilters.dateFrom || undefined}
                    onChange={({ currentTarget }) => {
                      const dateTo = currentTarget.value;
                      setHistoryFilters((filters) => ({ ...filters, dateTo }));
                    }}
                  />
                </label>
                <label>
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.MEMBER')}
                  <select
                    value={historyFilters.memberId || ''}
                    onInput={({ currentTarget }) => {
                      const memberId = currentTarget.value
                        ? Number(currentTarget.value)
                        : undefined;
                      setHistoryFilters((filters) => ({
                        ...filters,
                        memberId
                      }));
                    }}>
                    <option value="">{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.ALL_MEMBERS')}</option>
                    {memberOptions.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.RESULT')}
                  <select
                    value={historyFilters.accessResult}
                    onInput={({ currentTarget }) => {
                      const accessResult = parseAccessResultFilter(currentTarget.value);
                      setHistoryFilters((filters) => ({ ...filters, accessResult }));
                    }}>
                    <option value="all">
                      {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.ALL_RESULTS')}
                    </option>
                    <option value="granted">
                      {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.GRANTED')}
                    </option>
                    <option value="denied">{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.DENIED')}</option>
                  </select>
                </label>
                <label className={styles.searchFilter}>
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.SEARCH')}
                  <span>
                    <i className="pi pi-search" aria-hidden="true" />
                    <input
                      type="search"
                      value={historyFilters.search}
                      placeholder={t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.SEARCH_PLACEHOLDER')}
                      onChange={({ currentTarget }) => {
                        const search = currentTarget.value;
                        setHistoryFilters((filters) => ({ ...filters, search }));
                      }}
                    />
                  </span>
                </label>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => setHistoryFilters(emptyHistoryFilters)}>
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.CLEAR_FILTERS')}
                </button>
              </div>
              {newHistoryEventCount > 0 && (
                <button
                  type="button"
                  className={styles.newEventsButton}
                  onClick={showNewestHistory}>
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NEW_EVENTS', {
                    count: newHistoryEventCount
                  })}
                </button>
              )}
              <div
                className={`${styles.historyTableHeader} ${styles.fullHistoryHeader}`}
                aria-hidden="true">
                <span>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.RESULT')}</span>
                <span>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.TIME')}</span>
                <span>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.DOOR')}</span>
                <span>{t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.MEMBER_NAME')}</span>
              </div>
              {historyEvents.status === 'loading' ? (
                <EmptyState icon="pi-spin pi-spinner">
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LOADING')}
                </EmptyState>
              ) : historyEvents.status === 'error' ? (
                <EmptyState icon="pi-exclamation-circle">
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.HISTORY_UNAVAILABLE')}
                </EmptyState>
              ) : historyItems.length > 0 ? (
                <div className={styles.fullHistoryRows}>
                  {historyItems.map((event) => (
                    <HistoryRow
                      key={event.id}
                      event={event}
                      onOpenProfile={openProfile}
                      onLink={isGymKey ? undefined : () => setOverlay({ kind: 'link', event })}
                      t={t}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState icon="pi-filter-slash">
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NO_MATCHING_EVENTS')}
                </EmptyState>
              )}
              <footer className={styles.pagination}>
                <span>
                  {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.EVENT_COUNT', { count: historyTotal })}
                </span>
                <div>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    disabled={historyPageIndex === 0}
                    onClick={goToPreviousHistoryPage}>
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.PREVIOUS')}
                  </button>
                  <span>
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.PAGE_NUMBER', {
                      page: historyPageIndex + 1
                    })}
                  </span>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    disabled={
                      historyItems.length < HISTORY_PAGE_SIZE ||
                      (historyPageIndex + 1) * HISTORY_PAGE_SIZE >= historyTotal
                    }
                    onClick={goToNextHistoryPage}>
                    {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.NEXT')}
                  </button>
                </div>
              </footer>
            </DashboardOverlay>
          )}
        </div>
      </FullScreen>
    </div>
  );
};

const HistoryRow = ({
  event,
  onOpenProfile,
  onLink,
  t
}: {
  event: HistoryEvent;
  onOpenProfile: (memberId?: number) => void;
  onLink?: () => void;
  t: (key: string) => string;
}) => (
  <div className={`${styles.historyRow} ${!event.accessGranted ? styles.deniedHistoryRow : ''}`}>
    <span className={styles.historyResultCell}>
      <span
        className={`${styles.accessResultBadge} ${
          event.accessGranted ? styles.grantedResult : styles.deniedResult
        }`}
        title={
          event.accessGranted
            ? undefined
            : event.accessFailureMessage ||
              t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.ACCESS_DENIED_FALLBACK')
        }>
        <span
          className={`pi ${event.accessGranted ? 'pi-check-circle' : 'pi-times-circle'}`}
          aria-hidden="true"
        />
        {event.accessGranted
          ? t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.GRANTED')
          : t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.DENIED')}
      </span>
    </span>
    <time dateTime={event.doorOpenedTime}>
      <strong>{formatEventTime(event.doorOpenedTime)}</strong>
      <span>{formatEventDate(event.doorOpenedTime)}</span>
    </time>
    <span>{event.doorName || '—'}</span>
    <span>
      {event.memberId && event.memberName ? (
        <button
          type="button"
          className={styles.memberLink}
          onClick={() => onOpenProfile(event.memberId)}>
          {event.memberName}
        </button>
      ) : onLink ? (
        <button type="button" className={styles.linkMemberButton} onClick={onLink}>
          <span className="pi pi-link" aria-hidden="true" />
          {t('EMPLOYEE_DASHBOARD_PAGE.DASHBOARD.LINK_MEMBER')}
        </button>
      ) : (
        <span>{event.memberEmail || '—'}</span>
      )}
    </span>
  </div>
);

const DoorUnlockEventsDashboardPage = () => <MainColumn />;

export default DoorUnlockEventsDashboardPage;
